import {
	addItemBulkShoppingCart,
	deleteCartShoppingItems,
	getOrderRefresh,
} from '../services/sales.service';
import { changeShoppingCartStatus } from '../services/sales.service';
import { useDispatch, useSelector } from 'react-redux';
import {
	setAllOrders,
	setIsLoadingAddToCartV2,
	setIsLoadingOrderCheckout,
	setOrderCheckout,
} from '../redux/slices/cart.slice';
import { RootState } from '../redux/store';
import { showAlert, type AlertPayload } from '../redux/slices/alert.slice';
import { ALERT_TYPE } from '../components/molecules/Alert';
import type { OrderRefresh } from '../@types/Order.type';
import useEvents from './useEvents';
import { EVENT_TYPES } from '../shared/constants/events';
import { postOrders } from '../services/sales.service';
import {
	offerOfCartError,
	postRegularizationCart,
} from '../features/regularization/offer/offer.service';
import { regularizationOfferKey } from '../features/regularization/offer/useRegularizationOffer';
import type { OfferCartRequest } from '../features/regularization/offer/types';
import { useQueryClient } from '@tanstack/react-query';
import {
	filterExpiredShoppingItems,
	filterValidPayloadItems,
} from '../shared/constants/expiringProducts';
import { toSnakeCase } from '../shared/utils/caseConverters';
import { isPackageLine } from '../features/regularization/offer/packageCart';
import { useCatalogProductMap } from './useCatalog';
import { getPlanBySlug } from '../shared/plans';
import { usePlanRegimeWarning } from '../shared/plans/usePlanRegimeFit';
import { planChangeMessage } from '../shared/plans/planChangeMessage';
import { invoicingPlanLabelOf } from '../features/invoicing/v2/constants/planFeatures';
import {
	isInvoicingPlanAlreadyActive,
	PLAN_CHANGE_SUPPORT_ENTRY,
	PLAN_CHANGE_UNAVAILABLE,
	planChangeSupportDraft,
	type InvoiceQuota,
} from '../features/invoicing/v2/lib/quota';
import { QUOTA_QUERY_KEY } from '../features/invoicing/v2/services/invoicing.service';

// `source` comes from the payload itself (buildCheckoutPayload stamps it, and
// it is a typed CartSource), not from the URL: the front event and the
// shopping_item row name the surface the same way, so the funnel joins the
// sale without a translation table.
const sourcesOf = (items: any[]) =>
	[
		...new Set(
			items.map((item) => item?.additional_information?.source).filter(Boolean),
		),
	].join(',');

// The 412 comes from the plan line of the cart, so the target is the first line
// with a product version.
const targetPlanVersionIdOf = (items: any[] | undefined): number | undefined =>
	items?.find((item) => typeof item?.product_version_id === 'number')?.product_version_id;

const useAddToCartV2 = () => {
	const dispatch = useDispatch();
	const { allOrders } = useSelector((state: RootState) => state.cart);
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	const { trackEvent } = useEvents();
	const queryClient = useQueryClient();
	const { byPv: catalogByPv, data: catalog } = useCatalogProductMap();
	const warnPlanRegime = usePlanRegimeWarning();

	const pruneExpiredItemsFromOrder = async (order: any) => {
		const expiredItems = filterExpiredShoppingItems(order?.shopping_item);
		if (!expiredItems.length || !order?.id) return null;

		const itemIds = expiredItems.map((item) => ({ shopping_item_id: item.id }));
		try {
			await deleteCartShoppingItems(itemIds, order.id);
			return await getOrderRefresh(
				'?status=open&status=failed&is_renewal=false',
			);
		} catch {
			return null;
		}
	};

	const getOpenOrder = async () => {
		try {
			dispatch(setIsLoadingOrderCheckout(true));
			let orders = await getOrderRefresh(
				'?status=open&status=failed&is_renewal=false',
			);

			const refreshedAfterPrune = await pruneExpiredItemsFromOrder(orders?.[0]);
			if (refreshedAfterPrune) {
				orders = refreshedAfterPrune;
			}

			dispatch(setAllOrders(orders));
			dispatch(setOrderCheckout(orders?.[0]));
			return orders;
		} catch (error) {
			return [];
		} finally {
			dispatch(setIsLoadingOrderCheckout(false));
		}
	};

	// In parallel: each close is independent, and serially they cost one network
	// round-trip per order before the checkout could open.
	const closeOrders = async (ordersToClose?: any[]) => {
		try {
			const orders = ordersToClose ?? allOrders;
			await Promise.all(
				orders
					.filter((order: any) => order?.id)
					.map((order: any) =>
						changeShoppingCartStatus({
							shoppingCartId: order.id,
							status: 'closed',
						}),
					),
			);
		} catch (error) {}
	};

	/**
	 * The sales engine's 412 to a second invoicing plan is not an error the user can
	 * fix. With the target plan known the button asks for the change (a message to
	 * the general chat); without it, or if that fails, it is a support chat
	 * draft. The draft names the plan when the quota is cached.
	 */
	const planChangeUnavailableAlert = (targetProductVersionId?: number): AlertPayload => {
		const quota = queryClient.getQueryData<InvoiceQuota>(QUOTA_QUERY_KEY);
		const boughtPlan = quota?.plan.source === 'subscription' ? invoicingPlanLabelOf(quota.plan) : undefined;
		if (targetProductVersionId) {
			const targetPlan = catalog?.products.find(
				(plan) =>
					plan.monthly?.product_version_id === targetProductVersionId ||
					plan.annual?.product_version_id === targetProductVersionId,
			);
			const targetPeriod = targetPlan?.annual?.product_version_id === targetProductVersionId ? 'anual' : 'mensual';
			const targetLabel = targetPlan ? `${targetPlan.display_name ?? targetPlan.slug} ${targetPeriod}` : 'otro plan de facturación';
			return {
				type: ALERT_TYPE.INFO,
				title: 'Ya cuentas con un plan de facturación activo',
				description: 'Tu asesor puede ayudarte a cambiarlo.',
				duration: 10000,
				supportChat: {
					label: 'Solicitar cambio',
					entryPoint: PLAN_CHANGE_SUPPORT_ENTRY,
					draft: planChangeSupportDraft(boughtPlan),
					planChange: {
						targetProductVersionId,
						requestEntryPoint: 'invoicing_plan_change',
						message: planChangeMessage({ from: boughtPlan, to: targetLabel }),
					},
				},
			};
		}
		return {
			type: ALERT_TYPE.INFO,
			title: PLAN_CHANGE_UNAVAILABLE,
			description: 'Ya tienes un plan de facturación activo.',
			duration: 10000,
			supportChat: {
				label: 'Habla con nosotros',
				entryPoint: PLAN_CHANGE_SUPPORT_ENTRY,
				draft: planChangeSupportDraft(boughtPlan),
			},
		};
	};

	/**
	 * `eventPayload` is merged into the build's outcome events
	 * (`checkout_cart_item_added` / `checkout_add_to_cart_failed`), so a caller
	 * can say how the build was requested. Both events carry `duration_ms`, the
	 * time this build took, and `source`.
	 */
	const addToCart = async ({
		payload,
		eventPayload = {},
	}: {
		payload: any;
		eventPayload?: Record<string, unknown>;
	}) => {
		const startedAt = Date.now();
		const buildOutcome = (items: any[]) => ({
			...eventPayload,
			source: sourcesOf(items),
			duration_ms: Date.now() - startedAt,
		});
		try {
			dispatch(setIsLoadingAddToCartV2(true));

			const validPayload = filterValidPayloadItems(payload);
			if (!validPayload.length) {
				trackEvent({
					eventName: EVENT_TYPES.CHECKOUT_ADD_TO_CART_FAILED,
					eventPayload: {
						...buildOutcome(payload ?? []),
						error_type: 'no_valid_items',
						product_count: payload?.length || 0,
					},
				});
				dispatch(
					showAlert({
						type: ALERT_TYPE.ERROR,
						title: 'Producto no disponible',
						description:
							'Este producto ya no está disponible para compra.',
					}),
				);
				return;
			}

			const currentOrders = await getOpenOrder();
			const SESSION_WINDOW_MS = 12 * 60 * 60 * 1000;
			const reusableOpenOrder = currentOrders?.find(
				(order: any) =>
					order?.status === 'open' &&
					order?.created_at &&
					Date.now() - new Date(order.created_at).getTime() <
						SESSION_WINDOW_MS,
			);

			// El paquete Regularización total NUNCA comparte carrito (el motor lo
			// arma solo y rechaza líneas de otras rutas): lo que llegue después de
			// él empieza un carrito nuevo.
			const openCartHasPackage = reusableOpenOrder?.shopping_item?.some(
				(item: any) => isPackageLine(item?.additional_information),
			);
			let newOpenOrder = openCartHasPackage ? undefined : reusableOpenOrder;
			if (!newOpenOrder?.id) {
				await closeOrders(currentOrders);
				newOpenOrder = await postOrders({
					status: 'open',
					source_channel: 'web',
				});
			}

			if (newOpenOrder?.id) {
				const orderWithProducts = await addItemBulkShoppingCart(
					newOpenOrder?.id!,
					validPayload,
				);

				const normalized = toSnakeCase(orderWithProducts);
				const orders = Array.isArray(normalized)
					? (normalized as OrderRefresh[])
					: [];

				dispatch(setOrderCheckout(orders[0] ?? null));

				// Plan que no cubre el régimen del usuario: se avisa, no se frena, y solo
				// ya en el carrito (si el carrito falla, el error es el único aviso).
				// /planes ya avisó y confirmó antes; los planes de aliado y el incluido
				// en «Ponte al día» vienen fijados a propósito.
				validPayload.some((item: any) => {
					const info = item?.additional_information;
					if (info?.ally || info?.package_line) return false;
					if (info?.source === 'plans_subscriptions') return false;
					const slug = catalogByPv.get(item?.product_version_id)?.slug;
					const family = getPlanBySlug(slug)?.family;
					return !!family && warnPlanRegime(family, info?.source ?? 'unknown');
				});

				trackEvent({
					eventName: EVENT_TYPES.CHECKOUT_CART_ITEM_ADDED,
					eventPayload: {
						...buildOutcome(validPayload),
						order_id: newOpenOrder?.id,
						product_count: validPayload.length,
						product_version_ids: validPayload
							.map((item: any) => item.product_version_id)
							.join(','),
						total_amount: orders[0]?.total ? orders[0].total / 100 : 0,
					},
				});

				return orders;
			}
			trackEvent({
				eventName: EVENT_TYPES.CHECKOUT_ADD_TO_CART_FAILED,
				eventPayload: {
					...buildOutcome(validPayload),
					error_type: 'order_not_created',
					product_count: validPayload.length,
				},
			});
		} catch (error: any) {
			trackEvent({
				eventName: EVENT_TYPES.CHECKOUT_ADD_TO_CART_FAILED,
				eventPayload: {
					...buildOutcome(payload ?? []),
					error_type: 'request_failed',
					error_message: error?.response?.data?.message || error?.message,
					product_count: payload?.length || 0,
				},
			});

			dispatch(
				showAlert(
					isInvoicingPlanAlreadyActive(error)
						? planChangeUnavailableAlert(targetPlanVersionIdOf(payload))
						: {
								type: ALERT_TYPE.ERROR,
								title: 'Error',
								description:
									error?.response?.data?.message ||
									'Error al agregar el producto al carrito',
							},
				),
			);
			throw error;
		} finally {
			dispatch(setIsLoadingAddToCartV2(false));
			getOpenOrder();
		}
	};

	// The engine's message for a failed build: a string `message` (Nest format)
	// or `detail`. A validation array is never shown as is.
	const engineMessage = (error: any): string | undefined => {
		const data = error?.response?.data;
		for (const value of [data?.message, data?.detail]) {
			if (typeof value === 'string' && value) return value;
		}
		return undefined;
	};

	/**
	 * Builds the Regularización total cart on the server
	 * (`POST /regularization/cart`) and leaves it as the checkout's order. Unlike
	 * `addToCart` it neither closes nor creates carts here: the engine closes the
	 * user's draft carts and creates the package cart, at the charge the screen
	 * showed. Same outcome events as `addToCart`, plus `reused`.
	 *
	 * A refusal comes with the new offer: a 409 when the price changed (or the
	 * offer is no longer sellable), a 503 when it must be retried. That offer
	 * replaces the cached one at once, so the screen shows what the engine says
	 * now, and the alert says why; the offer is then quoted again.
	 */
	const addPackageToCart = async ({
		request,
		eventPayload = {},
	}: {
		request: OfferCartRequest;
		eventPayload?: Record<string, unknown>;
	}) => {
		const startedAt = Date.now();
		const source = 'ponte_al_dia';
		try {
			dispatch(setIsLoadingAddToCartV2(true));
			const { cart_id, reused, offer } = await postRegularizationCart(request);
			const openOrders: any[] = (await getOpenOrder()) ?? [];
			const cart = openOrders.find((order) => order?.id === cart_id);
			const orders = cart ? [cart, ...openOrders.filter((order) => order !== cart)] : openOrders;
			if (cart) dispatch(setOrderCheckout(cart));
			const items: any[] = cart?.shopping_item ?? [];
			trackEvent({
				eventName: EVENT_TYPES.CHECKOUT_CART_ITEM_ADDED,
				eventPayload: {
					...eventPayload,
					source,
					duration_ms: Date.now() - startedAt,
					order_id: cart_id,
					product_count: items.length,
					product_version_ids: items.map((item) => item?.product_version_id).join(','),
					total_amount: (offer.total?.charge_cents ?? 0) / 100,
					built_by: 'server',
					reused,
				},
			});
			return orders;
		} catch (error: any) {
			const status = error?.response?.status;
			const offer = offerOfCartError(error);
			if (offer) {
				const key = regularizationOfferKey(userId, request.declared);
				queryClient.setQueryData(key, offer);
				// The refusal's offer can be the repriced cart's, net of its heru cash,
				// while a new cart is checked against the charge before it: the next
				// read re-quotes, as the engine's message asks.
				void queryClient.invalidateQueries({ queryKey: key });
			}
			trackEvent({
				eventName: EVENT_TYPES.CHECKOUT_ADD_TO_CART_FAILED,
				eventPayload: {
					...eventPayload,
					source,
					duration_ms: Date.now() - startedAt,
					error_type: status === 409 ? 'quote_changed' : 'request_failed',
					error_message: engineMessage(error) ?? error?.message,
					built_by: 'server',
					...(offer ? { status: offer.status, reason: offer.reason } : {}),
				},
			});
			dispatch(
				showAlert({
					type: ALERT_TYPE.ERROR,
					title: status === 409 ? 'Tu paquete cambió' : 'Error',
					description:
						engineMessage(error) ?? 'No pudimos armar tu paquete de Regularización total',
				}),
			);
			void getOpenOrder();
			throw error;
		} finally {
			dispatch(setIsLoadingAddToCartV2(false));
		}
	};

	return {
		addToCart,
		addPackageToCart,
		getOpenOrder,
		closeOrders,
	};
};

export default useAddToCartV2;
